import type { Permission } from '@crm/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useRouter,
} from '@tanstack/react-router'
import {
  Bell,
  FileText,
  Inbox,
  LayoutDashboard,
  Link as LinkIcon,
  LogOut,
  Megaphone,
  Menu,
  Moon,
  ScrollText,
  Settings as SettingsIcon,
  Sun,
  Users,
  Users2,
  X,
} from 'lucide-react'
import { type ComponentType, useState } from 'react'
import { Logo } from '@/components/logo'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/field'
import { clientsCountQuery } from '@/features/clients/api'
import { unreadAlertCountQuery } from '@/features/alerts/api'
import { api } from '@/lib/api'
import { fetchMe } from '@/lib/auth-query'
import { useTheme } from '@/lib/theme'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    const me = await fetchMe(context.queryClient)
    if (!me) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
    return { me }
  },
  component: AppLayout,
})

interface NavItem {
  to: string
  label: string
  icon: ComponentType<{ className?: string }>
  permission?: Permission
  badge?: 'pending' | 'alerts'
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/clients', label: 'Clients', icon: Users2, permission: 'clients:view' },
  { to: '/review', label: 'Review', icon: Inbox, permission: 'clients:review', badge: 'pending' },
  { to: '/links', label: 'Links', icon: LinkIcon, permission: 'links:view' },
  { to: '/campaigns', label: 'Campaigns', icon: Megaphone, permission: 'campaigns:view' },
  { to: '/templates', label: 'Templates', icon: FileText, permission: 'templates:view' },
  { to: '/users', label: 'Staff', icon: Users, permission: 'users:manage' },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, permission: 'settings:manage' },
  { to: '/alerts', label: 'Alerts', icon: Bell, permission: 'alerts:view', badge: 'alerts' },
  { to: '/audit', label: 'Audit log', icon: ScrollText, permission: 'audit:view' },
]

const BOTTOM_NAV: NavItem[] = [
  { to: '/', label: 'Home', icon: LayoutDashboard },
  { to: '/clients', label: 'Clients', icon: Users2, permission: 'clients:view' },
  { to: '/campaigns', label: 'Campaigns', icon: Megaphone, permission: 'campaigns:view' },
  { to: '/alerts', label: 'Alerts', icon: Bell, permission: 'alerts:view', badge: 'alerts' },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, permission: 'settings:manage' },
]

function AppLayout() {
  const { me } = Route.useRouteContext()
  const navigate = useNavigate()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const { theme, toggle } = useTheme()

  const logout = useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSettled: async () => {
      queryClient.clear()
      await router.invalidate()
      navigate({ to: '/login', search: {} })
    },
  })

  const visibleNav = NAV.filter(
    (item) => !item.permission || me.permissions.includes(item.permission),
  )

  const visibleBottomNav = BOTTOM_NAV.filter(
    (item) => !item.permission || me.permissions.includes(item.permission),
  )

  const pending = useQuery({
    ...clientsCountQuery({ status: ['pending_review'] }),
    enabled: me.permissions.includes('clients:review'),
    refetchInterval: 60_000,
  })

  const unreadAlerts = useQuery({
    ...unreadAlertCountQuery,
    enabled: me.permissions.includes('alerts:view'),
  })

  return (
    <div className="flex min-h-dvh">
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 shrink-0 border-r border-[var(--color-sidebar-ring)] bg-[var(--color-sidebar)] transition-transform duration-300 ease-smooth md:relative md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-[var(--color-sidebar-ring)] px-5">
          <Logo />
          <button
            onClick={() => setMobileOpen(false)}
            className="rounded-lg p-1.5 text-[var(--color-sidebar-foreground)] hover:bg-[var(--color-sidebar-accent)] md:hidden"
            aria-label="Close sidebar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="space-y-0.5 p-3">
          {visibleNav.map(({ to, label, icon: Icon, badge }) => {
            const count =
              badge === 'pending'
                ? (pending.data?.total ?? 0)
                : badge === 'alerts'
                  ? (unreadAlerts.data?.count ?? 0)
                  : 0
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-[var(--color-sidebar-foreground)] transition-all duration-150 hover:bg-[var(--color-sidebar-accent)] hover:text-[var(--color-sidebar-accent-foreground)]"
                activeProps={{
                  className:
                    'bg-[var(--color-sidebar-accent)] font-medium !text-[var(--color-sidebar-accent-foreground)]',
                }}
                activeOptions={{ exact: to === '/' }}
              >
                <Icon className="h-4 w-4 shrink-0 opacity-60 transition-all duration-150 group-hover:opacity-100" />
                {label}
                {count > 0 && (
                  <span className="ml-auto rounded-full bg-[var(--color-primary)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--color-primary-foreground)]">
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 border-t border-[var(--color-sidebar-ring)] bg-[var(--color-sidebar)] p-3">
          <div className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary-muted)] text-xs font-semibold text-[var(--color-primary)]">
              {me.user.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[var(--color-foreground)]">
                {me.user.name}
              </p>
              <Badge variant="muted" className="text-[10px]">
                {me.user.role}
              </Badge>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-[var(--color-border)] bg-[var(--color-background)]/70 px-4 backdrop-blur-lg md:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="rounded-lg p-2 text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] md:hidden"
              aria-label="Open sidebar"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="hidden min-w-0 md:block">
              <p className="truncate text-sm font-medium">{me.user.name}</p>
              <p className="truncate text-xs text-[var(--color-muted-foreground)]">
                {me.user.email}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {me.permissions.includes('alerts:view') && (
              <Link to="/alerts" className="relative">
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Bell className="h-4 w-4" />
                  {(unreadAlerts.data?.count ?? 0) > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-destructive)] px-1 text-[10px] font-semibold text-[var(--color-destructive-foreground)]">
                      {unreadAlerts.data!.count > 99 ? '99+' : unreadAlerts.data!.count}
                    </span>
                  )}
                </Button>
              </Link>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={toggle}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            </Button>

            <div className="relative border-l border-[var(--color-border)] pl-2">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-primary-muted)] text-xs font-semibold text-[var(--color-primary)] transition-opacity hover:opacity-80"
                aria-label="Profile menu"
              >
                {me.user.name.charAt(0).toUpperCase()}
              </button>

              {profileOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
                  <div className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-1 shadow-lg">
                    <div className="px-3 py-2.5">
                      <p className="truncate text-sm font-medium text-[var(--color-foreground)]">
                        {me.user.name}
                      </p>
                      <p className="truncate text-xs text-[var(--color-muted-foreground)]">
                        {me.user.email}
                      </p>
                      <Badge variant="muted" className="mt-1.5 text-[10px]">
                        {me.user.role}
                      </Badge>
                    </div>
                    <div className="border-t border-[var(--color-border)]" />
                    <button
                      onClick={() => logout.mutate()}
                      disabled={logout.isPending}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)] disabled:opacity-50"
                    >
                      <LogOut className="h-4 w-4" />
                      {logout.isPending ? 'Signing out…' : 'Sign out'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <main
          key={router.state.location.pathname}
          className="animate-page-enter flex-1 overflow-auto p-4 md:p-6 lg:p-8"
        >
          <Outlet />
        </main>

        <nav className="sticky bottom-0 z-30 flex items-center justify-around border-t border-[var(--color-border)] bg-[var(--color-background)]/70 backdrop-blur-lg md:hidden">
          {visibleBottomNav.map(({ to, label, icon: Icon, badge }) => {
            const count = badge === 'alerts' ? (unreadAlerts.data?.count ?? 0) : 0
            return (
              <Link
                key={to}
                to={to}
                className="relative flex flex-col items-center gap-0.5 px-3 py-2 text-[10px] text-[var(--color-muted-foreground)] transition-colors"
                activeProps={{
                  className: '!text-[var(--color-primary)]',
                }}
                activeOptions={{ exact: to === '/' }}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{label}</span>
                {count > 0 && (
                  <span className="absolute right-1 top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-[var(--color-destructive)] px-1 text-[8px] font-bold text-[var(--color-destructive-foreground)]">
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>
      </div>
    </div>
  )
}
